import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import { PublicClientApplication, EventType } from '@azure/msal-browser';
import { msalConfig } from './config/authConfig';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import 'rsuite/dist/rsuite.min.css';
import './index.css';

// Dev-only: suppress noisy ResizeObserver loop errors that can spam the overlay.
// These are usually benign and triggered by rapid layout changes.
if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
    const resizeObserverErrorRe = /ResizeObserver loop (limit exceeded|completed with undelivered notifications)/i;

    window.addEventListener('error', (event) => {
        const msg = String(event?.message || '');
        if (resizeObserverErrorRe.test(msg)) {
            event.preventDefault?.();
            event.stopImmediatePropagation?.();
        }
    });

    const originalConsoleError = console.error;
    console.error = (...args) => {
        const first = args?.[0];
        if (typeof first === 'string' && resizeObserverErrorRe.test(first)) return;
        return originalConsoleError(...args);
    };
}

/**
 * MSAL should be instantiated outside of the component tree to prevent it from being re-instantiated on re-renders.
 * For more, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/getting-started.md
 */
const msalInstance = new PublicClientApplication(msalConfig);

/**
 * Create QueryClient for React Query - best practice is to create it outside component tree
 */
const queryClient = new QueryClient();

// Default to using the first account if no account is active on page load
if (!msalInstance.getActiveAccount() && msalInstance.getAllAccounts().length > 0) {
    // Account selection logic is app dependent. Adjust as needed for different use cases.
    msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
}

// Listen for sign-in event and set active account
msalInstance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload.account) {
        const account = event.payload.account;
        msalInstance.setActiveAccount(account);
    }
});

const root = createRoot(document.getElementById('root'));
root.render(
    <QueryClientProvider client={queryClient}>
        <App instance={msalInstance}/>
    </QueryClientProvider>
);